import React, { useEffect, useRef } from 'react';
import { Box, useTheme, keyframes } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface AnimatedBackgroundProps {
  variant?: 'particles' | 'gradient' | 'waves';
}

/**
 * A component that renders an animated background
 * Compatible with the theme system and provides various animation styles
 */
const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  variant = 'particles'
}) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestIdRef = useRef<number>(0);
  
  // Define animations as keyframes
  const gradientAnimation = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;
  
  const waveAnimation = keyframes`
    0% { background-position: 0% 0%; }
    100% { background-position: 100% 100%; }
  `;
  
  // Particles animation
  useEffect(() => {
    if (variant !== 'particles' || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    // Particle configuration
    const particleCount = Math.floor(window.innerWidth / 20); // Responsive particle count
    const particleBaseSize = Math.min(canvas.width, canvas.height) / 200;
    
    // Determine colors based on theme
    const isDarkMode = theme.palette.mode === 'dark';
    const particleColor = isDarkMode 
      ? 'rgba(255, 255, 255, 0.3)' 
      : 'rgba(25, 118, 210, 0.15)';
    const lineColor = isDarkMode 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(25, 118, 210, 0.07)';
    
    // Create particles
    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: Math.random() * particleBaseSize + particleBaseSize / 2,
      speedX: Math.random() * 0.5 - 0.25,
      speedY: Math.random() * 0.5 - 0.25,
      opacity: Math.random() * 0.5 + 0.3
    }));
    
    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw and update particles
      particles.forEach((particle, i) => {
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particleColor;
        ctx.fill();
        
        // Connect particles that are close to each other
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = 150; // Maximum distance for drawing a connection
          
          if (distance < maxDistance) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = 1 * (1 - distance / maxDistance); // Line fades with distance
            ctx.stroke();
          }
        });
      });
      
      requestIdRef.current = requestAnimationFrame(animate);
    };
    
    // Start animation
    animate();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(requestIdRef.current);
    };
  }, [variant, theme.palette.mode]);
  
  // Get background styles based on variant
  const getBackgroundStyles = (): SxProps<Theme> => {
    if (variant === 'gradient') {
      const primaryColor = theme.palette.mode === 'dark' 
        ? 'rgba(25, 32, 45, 0.8)' 
        : 'rgba(245, 247, 250, 0.8)';
      const accentColor = theme.palette.mode === 'dark'
        ? 'rgba(10, 15, 30, 0.9)'
        : 'rgba(230, 235, 245, 0.9)';
      
      return {
        background: `linear-gradient(135deg, ${primaryColor}, ${accentColor}, ${primaryColor})`,
        backgroundSize: '400% 400%',
        animation: `${gradientAnimation} 15s ease infinite`,
      };
    }
    
    if (variant === 'waves') {
      return {
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `radial-gradient(circle at center, ${
            theme.palette.mode === 'dark' 
              ? 'rgba(50, 50, 70, 0.1) 0%, rgba(10, 10, 20, 0) 70%' 
              : 'rgba(100, 150, 200, 0.1) 0%, rgba(250, 250, 250, 0) 70%'
          })`,
          backgroundSize: '20vmin 20vmin',
          opacity: 0.8,
          zIndex: -1,
          animation: `${waveAnimation} 30s linear infinite`,
        },
        '&::after': {
          backgroundSize: '15vmin 15vmin',
          animation: `${waveAnimation} 20s linear infinite`,
        },
      };
    }
    
    // Default styles for particles
    return {};
  };
  
  // Base styles that are common to all variants
  const baseStyles: SxProps<Theme> = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
    overflow: 'hidden',
    pointerEvents: 'none', // Allows interaction with elements above the background
    transition: 'all 0.5s ease',
  };
  
  return (
    <Box
      sx={{
        ...baseStyles,
        ...getBackgroundStyles()
      }}
    >
      {variant === 'particles' && (
        <canvas 
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
          }}
        />
      )}
    </Box>
  );
};

export default AnimatedBackground;